import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { colaboradoresAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ColaboradorFormDialog } from './ColaboradorFormDialog'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Phone,
  Mail,
  Users,
  Briefcase,
  Building2,
  Calendar,
  Loader2,
  TrendingDown,
  ArrowDownRight,
} from 'lucide-react'

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  CLT: 'CLT',
  PJ: 'Pessoa Jurídica (PJ)',
  ESTAGIO: 'Estágio',
  TEMPORARIO: 'Temporário',
  AUTONOMO: 'Autônomo',
}

const CONTRACT_TYPE_COLORS: Record<string, string> = {
  CLT: 'bg-blue-100 text-blue-700',
  PJ: 'bg-purple-100 text-purple-700',
  ESTAGIO: 'bg-green-100 text-green-700',
  TEMPORARIO: 'bg-amber-100 text-amber-700',
  AUTONOMO: 'bg-neutral-100 text-neutral-700',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat('pt-BR').format(new Date(dateStr))
}

export function ColaboradorDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const canEdit = usePermission('colaboradores:edit')
  const canDelete = usePermission('colaboradores:delete')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { data: colaborador, isLoading } = useQuery({
    queryKey: ['colaborador', id],
    queryFn: () => colaboradoresAPI.getById(id!),
    enabled: !!id,
  })

  const { data: financial } = useQuery({
    queryKey: ['colaborador-financial', id],
    queryFn: () => colaboradoresAPI.getFinancialSummary(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => colaboradoresAPI.delete(id!),
    onSuccess: () => {
      toast.success('Colaborador excluído!')
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] })
      navigate('/colaboradores')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!colaborador) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <p className="text-neutral-500">Colaborador não encontrado.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/colaboradores')}>
          Voltar
        </Button>
      </div>
    )
  }

  const totalSaidas = financial?.totalSaidas || 0
  const transacoes = financial?.transacoesRecentes || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/colaboradores')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold text-neutral-900">{colaborador.name}</h1>
              <Badge variant={colaborador.isActive ? 'default' : 'secondary'}>
                {colaborador.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            <p className="mt-1 text-neutral-500 text-sm">{formatDocument(colaborador.document)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="financeiro">Financeiro</TabsTrigger>
        </TabsList>

        {/* INFO TAB */}
        <TabsContent value="info" className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {colaborador.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-neutral-400" />
                    <span>{colaborador.email}</span>
                  </div>
                )}
                {colaborador.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-neutral-400" />
                    <span>{colaborador.phone}</span>
                  </div>
                )}
                {!colaborador.email && !colaborador.phone && (
                  <p className="text-sm text-neutral-400">Nenhum contato cadastrado.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados Profissionais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase className="h-4 w-4 text-neutral-400" />
                  <span>{colaborador.cargo}</span>
                </div>
                {colaborador.department && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-neutral-400" />
                    <span>{colaborador.department}</span>
                  </div>
                )}
                {colaborador.admissionDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-neutral-400" />
                    <span>Admissão: {formatDate(colaborador.admissionDate)}</span>
                  </div>
                )}
                <Badge
                  variant="outline"
                  className={CONTRACT_TYPE_COLORS[colaborador.contractType] || ''}
                >
                  {CONTRACT_TYPE_LABELS[colaborador.contractType] || colaborador.contractType}
                </Badge>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* FINANCEIRO TAB */}
        <TabsContent value="financeiro" className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-50 p-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Total de Despesas</p>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(totalSaidas)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {transacoes.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Transações Recentes</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Projeto</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transacoes.map((tx: any) => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-sm text-neutral-500 whitespace-nowrap">
                          {formatDate(tx.date)}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{tx.description}</TableCell>
                        <TableCell className="text-sm text-neutral-500">
                          {tx.project?.name || '—'}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />
                            <span className="text-red-600">{formatCurrency(tx.amount)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-12">
              <p className="text-neutral-500 text-sm">Nenhuma transação vinculada a este colaborador.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <ColaboradorFormDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        colaborador={colaborador}
      />

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Colaborador</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir <strong>{colaborador.name}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
